/**
 * F3 (14/08) — os SPECS dos blocos do cadastro, num módulo só.
 *
 * A resposta à Regra 12 pro wizard dinâmico (F4): a ficha e o wizard renderizam
 * OS MESMOS campos a partir DA MESMA lista. Antes, os specs moravam dentro do
 * ClientDetailPage e do FornecedorTab — portá-los pro wizard seria copiá-los, e
 * campo copiado é o desenho errado (amarra 1 da rodada).
 *
 * O que mora aqui: as listas de campos (specs) e os textos de ajuda dos blocos.
 * O que NÃO mora: o chrome (o card Editar/Salvar da ficha, o passo do wizard) —
 * são cascas diferentes por natureza; o compartilhado é o miolo.
 *
 * As fábricas continuam fábricas pelos motivos originais:
 *  · CAMPOS_BLOCO_PRINCIPAIS(isCpf, fantasiaIgual, semDocumento) — rótulo CPF/CNPJ, o M-5 e o documento vazio (#592);
 *  · camposDeEndereco(prefixo, buscarCep) — o "Buscar pelo CEP" precisa do
 *    useLookupCep, que é hook e só existe dentro de um componente.
 */

import type { CampoBlocoSpec } from '@/components/admin/ClienteBlocoEditavel';
import { formatPhone } from '@/lib/format-phone';
import { isValidCNPJ } from '@/lib/format-doc';
import { reaisStringToCentavos } from '@/lib/format';
import { SECOES_INVENTARIO } from '@/lib/clientes/inventario-campos';
import {
  LIMITE_RAZAO_SOCIAL_OMIE,
  mensagemRazaoLonga,
  sugerirRazaoAbreviada,
} from '@/lib/clientes/razao-social-omie';

const soDigitos = (v: string) => v.replace(/\D/g, '');

/**
 * F4 — conversão rascunho→valor de UM campo, com a validação de formato.
 * Motor ÚNICO: a ficha (montarPayload), o wizard dinâmico e o Cadastro rápido
 * convertem pelo mesmo caminho — duplicar isto é como o "5000 = R$ 50,00"
 * volta. Mora aqui (e não no ClienteBlocoEditavel) porque arquivo de
 * componente que exporta função quebra o fast-refresh — o gate barrou.
 */
export function converterRascunhoDeCampo(
  c: CampoBlocoSpec,
  bruto: string,
): { ok: true; valor: string | number | boolean | null } | { ok: false; erro: string } {
  if (c.tipo === 'simnao') {
    return { ok: true, valor: bruto === '' ? null : bruto === 'sim' };
  }
  if (c.tipo === 'numero') {
    if (bruto === '') return { ok: true, valor: null };
    const n = Number.parseInt(bruto, 10);
    if (Number.isNaN(n) || n < 0) {
      return { ok: false, erro: `${c.label}: use um número inteiro (0 ou mais).` };
    }
    return { ok: true, valor: n };
  }
  if (c.tipo === 'moeda') {
    if (bruto === '') return { ok: true, valor: null };
    const centavos = reaisStringToCentavos(bruto);
    if (!Number.isFinite(centavos) || centavos < 0) {
      return { ok: false, erro: `${c.label}: use um valor em reais (ex.: 5.000,00).` };
    }
    return { ok: true, valor: centavos };
  }
  if (c.tipo === 'data') {
    // O `<input type="date">` só entrega 'YYYY-MM-DD' ou ''. A validação aqui
    // é contra o que chega por outro caminho (colar, autofill): uma data
    // malformada gravada crua ficaria para sempre.
    if (bruto === '') return { ok: true, valor: null };
    if (!/^\d{4}-\d{2}-\d{2}$/.test(bruto)) {
      return { ok: false, erro: `${c.label}: use uma data válida.` };
    }
    return { ok: true, valor: bruto };
  }
  if (c.tipo === 'telefone') {
    const digitos = soDigitos(bruto);
    if (digitos !== '' && (digitos.length < 8 || digitos.length > 13)) {
      return { ok: false, erro: `${c.label}: use DDD + número (8 a 13 dígitos).` };
    }
    return { ok: true, valor: digitos === '' ? null : digitos };
  }
  return { ok: true, valor: bruto === '' ? null : bruto };
}

// ── Garantia e ajudas (A-3/L-4: a garantia é UMA constante, não três frases) ──

export const GARANTIA_EDICAO =
  'O que você corrigir aqui fica — um novo import do Omie não passa por cima.';
export const AJUDA_BLOCO_PRINCIPAIS =
  'Razão social, nome fantasia e telefone vieram do Omie. ' +
  GARANTIA_EDICAO +
  ' O nº de unidades é só do sistema — o Omie não tem esse campo. ' +
  'O CNPJ/CPF não se edita neste bloco.';
export const AJUDA_BLOCO_FISCAL =
  'IE, IM, Simples Nacional, contribuinte ICMS, produtor rural, SUFRAMA, tipo de atividade ' +
  'e CNAE vieram do Omie. ' +
  GARANTIA_EDICAO;
export const AJUDA_BLOCO_BANCARIOS =
  'Banco, agência, conta, chave Pix e titular vieram do Omie. ' +
  GARANTIA_EDICAO +
  ' Preencha quando houver reembolso ou repasse.';
export const AJUDA_DOCUMENTO_TRAVADO =
  'O CNPJ/CPF é a chave do cadastro e não muda por aqui. Documento errado se corrige ' +
  'pela unificação de cadastros — assim nenhum vínculo se perde no caminho.';
export const AJUDA_DOCUMENTO_VAZIO =
  'Este cadastro ainda não tem documento. Digite o CNPJ (14 números) ou o CPF (11), com ou sem pontos. Depois de salvo, ' +
  'ele vira a chave do cadastro e não muda mais por aqui. Se o número já estiver em outro cadastro, o sistema avisa.';
export const AJUDA_BLOCO_FUNCIONARIO =
  'Aparece porque este cadastro tem a etiqueta "Funcionário". São dados da pessoa ' +
  'como colaborador da IGM — nada disto vai para o Omie. Preencher a data de ' +
  'demissão marca quem saiu, mas NÃO desliga o cadastro: ele continua na lista, e o ' +
  'histórico da pessoa continua consultável.';
export const AJUDA_BLOCO_FATURAMENTO =
  'Campos de faturamento que vieram do Omie (transportadora, parcelas, limite de crédito, ' +
  'e-mail de NF-e…). O que você corrigir aqui fica — um novo import não passa por cima.';
export const AJUDA_BLOCO_FORNECEDOR =
  'Regras que este fornecedor aplica quando a IGM compra dele: a partir de quanto ele aceita ' +
  'faturar, quanto cobra de imposto e se exige nota fiscal. O Compras usa estes números para ' +
  'calcular o faturamento direto nas cotações.';

// ── Specs ────────────────────────────────────────────────────────────────

// ⚠️ `cargoFuncionario` e não `cargo`: `responsaveis.cargo` já existe e é o cargo
// do CONTATO do cliente (o síndico, o gerente predial). Dois cargos, duas pessoas
// diferentes, o mesmo nome — o rótulo diz de qual se trata.
export const CAMPOS_BLOCO_FUNCIONARIO: CampoBlocoSpec[] = [
  { name: 'cargoFuncionario', label: 'Cargo na IGM', full: true },
  { name: 'dataAdmissao', label: 'Data de admissão', tipo: 'data' },
  { name: 'dataDemissao', label: 'Data de demissão', tipo: 'data' },
  { name: 'dataAniversario', label: 'Data de aniversário', tipo: 'data' },
  { name: 'cltRegistro', label: 'Registro CLT', mono: true },
  { name: 'cboCodigo', label: 'Código CBO', mono: true },
];

// M-5 (REALINHAR-MAIN): `fantasiaIgual` some com o "Nome fantasia" na LEITURA
// quando ele repete a razão social. Na EDIÇÃO o campo volta.
// 🔴 #592 (19/10/2026): `semDocumento` — cadastro sem CNPJ/CPF nenhum pode PREENCHER o documento aqui (uma vez). Com
// documento, o campo continua travado: trocar é pela unificação. Antes ele era travado sempre, e o IATE CLUBE (criado à mão,
// sem documento) não tinha como receber o CNPJ.
// 🔴 #594 (19/10/2026): `buscarCnpj` — o documento vazio ganha o "Buscar dados pelo CNPJ" do wizard. Pedido do Matheus: "digitei
// o CNPJ e não tinha botão para buscar os dados". Ele completa o que está VAZIO neste bloco (razão social, nome fantasia,
// telefone); endereço e atividade moram em outros blocos e vêm do "Completar pelo CNPJ" do topo, que aparece depois de salvar.
export const CAMPOS_BLOCO_PRINCIPAIS = (
  isCpf: boolean,
  fantasiaIgual = false,
  semDocumento = false,
  buscarCnpj?: (cnpjDigitado: string) => Promise<Record<string, string> | null>,
): CampoBlocoSpec[] => [
  {
    name: 'razaoSocial',
    label: 'Razão social',
    full: true,
    obrigatorio: true,
    // Limite do Omie (15/08): 60 caracteres, sem truncar — contador + barra o
    // salvar SÓ quando o campo foi editado (payload parcial da ficha).
    ajuda:
      `O Omie — onde o cadastro é faturado — aceita no máximo ${LIMITE_RAZAO_SOCIAL_OMIE} caracteres na razão social. ` +
      `Por isso o sistema limita o campo: encurte mantendo o que identifica o cliente (ex.: "ASSOCIACAO DOS PROPRIETARIOS" vira "ASSOC DOS PROPR").`,
    contadorMax: LIMITE_RAZAO_SOCIAL_OMIE,
    validarAoSalvar: (valor) => {
      const s = typeof valor === 'string' ? valor.trim() : '';
      if (s.length <= LIMITE_RAZAO_SOCIAL_OMIE) return null;
      const sugestao = sugerirRazaoAbreviada(s);
      return sugestao
        ? `${mensagemRazaoLonga(s.length)} Sugestão: "${sugestao}".`
        : mensagemRazaoLonga(s.length);
    },
  },
  { name: 'nomeFantasia', label: 'Nome fantasia', full: true, soEdicao: fantasiaIgual },
  semDocumento
    ? {
        name: 'documento',
        label: 'CNPJ ou CPF',
        tipo: 'texto',
        mono: true,
        ajuda: AJUDA_DOCUMENTO_VAZIO,
        // 🔴 #597 (20/10/2026): linha própria. Na grade de três colunas (CNPJ | Telefone | Nº de unidades) o botão "Buscar dados
        // pelo CNPJ" (179 px) espremia o campo a 45 px num container de 232 px (medido no ar, RESIDENCIAL ÁGAPE, janela de 1920).
        full: true,
        ...(buscarCnpj && {
          acao: {
            rotulo: 'Buscar dados pelo CNPJ',
            descricao: 'Preencher razão social, nome fantasia e telefone vazios com os dados públicos da Receita',
            executar: buscarCnpj,
            soCamposVazios: true,
            // A Receita só consulta CNPJ: com CPF o botão não tem o que buscar (a mesma régua do wizard e do topo da ficha).
            habilitada: (v: string) => isValidCNPJ(v),
            mensagens: {
              preenchido:
                'Dados do CNPJ preenchidos. Confira e salve; depois, o botão "Completar pelo CNPJ" no topo da ficha preenche endereço e atividade.',
              naoAchou: 'A Receita não trouxe dados para este CNPJ. Preencha à mão.',
              nadaAPreencher:
                'Razão social, nome fantasia e telefone já estão preenchidos. Salve; depois, o botão "Completar pelo CNPJ" no topo da ficha preenche endereço e atividade.',
            },
          },
        }),
      }
    : {
        name: 'documento',
        label: isCpf ? 'CPF' : 'CNPJ',
        tipo: 'travado',
        mono: true,
        ajuda: AJUDA_DOCUMENTO_TRAVADO,
      },
  {
    name: 'telefone',
    label: 'Telefone',
    tipo: 'telefone',
    formatLeitura: (v) => (v ? formatPhone(String(v)) : null),
  },
  { name: 'numUnidades', label: 'Nº de unidades', tipo: 'numero' },
];

export const CAMPOS_BLOCO_FISCAL: CampoBlocoSpec[] = [
  { name: 'ie', label: 'Inscrição Estadual', mono: true },
  { name: 'im', label: 'Inscrição Municipal', mono: true },
  { name: 'optanteSimples', label: 'Simples Nacional', tipo: 'simnao' },
  { name: 'contribuinteIcms', label: 'Contribuinte ICMS', tipo: 'simnao' },
  { name: 'produtorRural', label: 'Produtor rural', tipo: 'simnao' },
  { name: 'suframa', label: 'SUFRAMA', mono: true },
  { name: 'tipoAtividade', label: 'Tipo de atividade', full: true },
  { name: 'cnae', label: 'CNAE principal', full: true },
];

export const CAMPOS_BLOCO_BANCARIOS: CampoBlocoSpec[] = [
  { name: 'banco', label: 'Banco' },
  { name: 'agencia', label: 'Agência', mono: true },
  { name: 'conta', label: 'Conta', mono: true },
  { name: 'pix', label: 'Chave Pix', mono: true },
  { name: 'titularNome', label: 'Titular' },
  // F5 (auditoria da matriz): editável pelo PATCH e auditado, mas sem UI antes.
  { name: 'titularDocumento', label: 'CNPJ/CPF do titular', mono: true },
];

// C-03 — "Faturamento e recomendações": campos que o import do Omie já trazia e
// que ficavam invisíveis na ficha.
export const CAMPOS_BLOCO_FATURAMENTO: CampoBlocoSpec[] = [
  {
    name: 'telefone2',
    label: 'Telefone 2',
    tipo: 'telefone',
    formatLeitura: (v) => (v ? formatPhone(String(v)) : null),
  },
  { name: 'website', label: 'Site' },
  { name: 'emailNfe', label: 'E-mail para NF-e' },
  { name: 'transportadora', label: 'Transportadora' },
  { name: 'parcelasPadrao', label: 'Parcelas padrão', tipo: 'numero' },
  { name: 'vendedorPadraoOmie', label: 'Vendedor padrão (Omie)' },
  { name: 'gerarBoleto', label: 'Gerar boleto', tipo: 'simnao' },
  { name: 'codigoReceita', label: 'Código de receita', mono: true },
  { name: 'limiteCreditoCentavos', label: 'Limite de crédito', tipo: 'moeda' },
  {
    name: 'bloquearFaturamento',
    label: 'Bloquear faturamento',
    tipo: 'simnao',
    ajuda:
      'Marca o cadastro como bloqueado para faturamento — é o mesmo campo do Omie. ' +
      'O sistema apenas espelha: nada aqui impede emitir nota por conta própria.',
  },
];

export const CAMPOS_BLOCO_FORNECEDOR: CampoBlocoSpec[] = [
  { name: 'fornFazManutencao', label: 'Faz manutenção de produtos?', tipo: 'simnao', full: true },
  { name: 'fornMinFaturarVendaCentavos', label: 'Mínimo para faturar — venda', tipo: 'moeda' },
  { name: 'fornMinFaturarManutencaoCentavos', label: 'Mínimo para faturar — manutenção', tipo: 'moeda' },
  { name: 'fornImpostoPct', label: '% de imposto', tipo: 'numero' },
  { name: 'fornCobraImpostoRetirada', label: 'Cobra imposto na retirada do crédito?', tipo: 'simnao' },
  { name: 'fornPedeNfRetirada', label: 'Pede NF na retirada do crédito?', tipo: 'simnao' },
  { name: 'fornPedeNfAbatimento', label: 'Pede NF no abatimento (compras futuras)?', tipo: 'simnao' },
];

/**
 * F4 — os campos EXIGIDOS pela régua que o formulário não mostra por conta
 * própria. É a metade "não recusa e não ignora" da decisão do Matheus: se a
 * config exige um campo que o form não tem fixo, o form MOSTRA o campo — no
 * wizard vira o passo "Campos exigidos", no Cadastro rápido vira o bloco
 * extra. Motor único: os specs vêm dos blocos da ficha (tipo, máscara,
 * conversão) e o RÓTULO vem do inventário — as mesmas palavras que o admin
 * viu na tela de config ("Endereço — logradouro", não "Logradouro" solto).
 *
 * Fora da lista, de propósito:
 *  · listas (e-mails/contatos/etiquetas) — têm UI própria em cada form;
 *  · `documento` — travado/com UI própria nos dois forms;
 *  · campo sem spec na ficha — não inventamos input aqui.
 */
export function specsExigidosForaDoForm(
  exigidos: { has(nome: string): boolean },
  cobertos: Iterable<string>,
): CampoBlocoSpec[] {
  const jaTem = new Set(cobertos);
  const porNome = new Map<string, CampoBlocoSpec>();
  const todos: CampoBlocoSpec[] = [
    ...CAMPOS_BLOCO_PRINCIPAIS(false),
    ...CAMPOS_BLOCO_FISCAL,
    ...CAMPOS_BLOCO_BANCARIOS,
    ...CAMPOS_BLOCO_FATURAMENTO,
    ...CAMPOS_BLOCO_FUNCIONARIO,
    ...CAMPOS_BLOCO_FORNECEDOR,
    ...camposDeEndereco('endereco', async () => null),
    ...camposDeEndereco('entrega', async () => null),
  ];
  for (const c of todos) {
    // Ação (ex.: "Buscar pelo CEP") e soEdicao são chrome da ficha — no bloco
    // de exigidos o campo entra cru.
    const { acao: _acao, soEdicao: _soEdicao, ...cru } = c;
    if (!porNome.has(cru.name)) porNome.set(cru.name, cru);
  }
  const vistos = new Set<string>();
  const resultado: CampoBlocoSpec[] = [];
  for (const secao of SECOES_INVENTARIO) {
    for (const campo of secao.campos) {
      if (vistos.has(campo.fieldName)) continue;
      vistos.add(campo.fieldName);
      if (campo.lista || campo.fieldName === 'documento') continue;
      if (jaTem.has(campo.fieldName) || !exigidos.has(campo.fieldName)) continue;
      const spec = porNome.get(campo.fieldName);
      if (!spec || spec.tipo === 'travado') continue;
      resultado.push({ ...spec, label: campo.label, full: false });
    }
  }
  return resultado;
}

// C-08 (a)/(b) — endereço fiscal e de entrega, o mesmo formulário apontando pra
// colunas diferentes. C-3: fábrica porque o "Buscar pelo CEP" precisa do hook.
export function camposDeEndereco(
  prefixo: 'endereco' | 'entrega',
  buscarCep: (cep: string) => Promise<Record<string, string> | null>,
): CampoBlocoSpec[] {
  const n = (sufixo: string) => `${prefixo}${sufixo}` as CampoBlocoSpec['name'];
  return [
    { name: n('Logradouro'), label: 'Logradouro', full: true },
    { name: n('Numero'), label: 'Número', maxLength: 20 },
    { name: n('Bairro'), label: 'Bairro', maxLength: 100 },
    {
      name: n('Cep'),
      label: 'CEP',
      mono: true,
      maxLength: 15,
      acao: {
        rotulo: 'Buscar',
        descricao: 'Preencher logradouro, bairro, cidade e UF a partir do CEP',
        executar: buscarCep,
      },
    },
    { name: n('Cidade'), label: 'Cidade', maxLength: 100 },
    // A UF aceita 2 caracteres no servidor; sem o limite aqui, "São Paulo" só
    // seria recusado depois de salvar, com um 400 sem graça.
    { name: n('Uf'), label: 'UF', maxLength: 2, maiusculas: true },
  ];
}
